import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useNavigate } from '@tanstack/react-router';

import { HOME_ROUTE } from '@/constants/routes';
import { googleCallback } from '@/api/auth';
import { syncUserFromServer } from '@/api/user';
import { saveToken } from '@/stores/secure';
import { useAuthStore } from '@/stores/AuthState';
import { getOAuthParam } from '@/utils/oauthUtils';
import type { GoogleTokens } from '@/types';

/**
 * Native deep-link auth handler. On the Capacitor shell the Google OAuth flow
 * runs in the system browser and Strapi redirects back to the app's custom
 * scheme (`racore.app://auth/callback?access_token=…`). `@capacitor/app`'s
 * `appUrlOpen` delivers that URL here; we finish the token exchange and route
 * home — the same logic as the web `/auth/callback` route.
 *
 * On web this listener never fires (no app URL opens), so it's a no-op.
 */
export function useDeepLinkAuth() {
  const navigate = useNavigate();
  const setUserInfo = useAuthStore((s) => s.setUserInfo);
  const language = useAuthStore((s) => s.userInfo.language) || 'fr';

  useEffect(() => {
    let remove: (() => void) | undefined;

    void App.addListener('appUrlOpen', async ({ url }) => {
      // D5 — an invitation universal link deep-links straight to the join screen.
      const inviteMatch = url.match(/\/invite\/([A-Za-z0-9-]+)/);
      if (inviteMatch) {
        navigate({ to: '/invite/$code', params: { code: inviteMatch[1] } });
        return;
      }
      if (!url.includes('auth/callback')) return;
      const tokens: GoogleTokens = {
        access_token: getOAuthParam(url, 'access_token') ?? undefined,
        refresh_token: getOAuthParam(url, 'refresh_token') ?? undefined,
        id_token: getOAuthParam(url, 'id_token') ?? undefined,
      };
      if (!tokens.access_token && !tokens.id_token) return;
      // The OAuth flow ran in the system browser (SFSafariViewController /
      // Custom Tab); close it now that we're back in the app. On iOS the
      // dismissal can race with the app returning to the foreground, so we
      // fire it immediately and retry once on the next tick.
      const closeBrowser = async () => {
        try {
          await Browser.close();
        } catch {
          /* already closed / nothing to close */
        }
      };
      void closeBrowser();
      setTimeout(() => void closeBrowser(), 300);
      try {
        const response = await googleCallback(tokens, language);
        await saveToken(response.jwt, response.refreshToken);
        await setUserInfo(response.user as never);
        // profile-sync-on-launch — the OAuth payload carries no favourite
        // service: re-read the full profile now that the session exists.
        void syncUserFromServer();
        navigate({ to: HOME_ROUTE, replace: true });
      } catch {
        // handled upstream (handleApiError) — user can retry
      }
    }).then((handle) => {
      remove = () => void handle.remove();
    });

    return () => remove?.();
  }, [navigate, setUserInfo, language]);
}
